import React, { useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import SectionCard from '../../components/SectionCard';
import WorkflowPipeline from '../../components/WorkflowPipeline';
import QuickActionCard from '../../components/QuickActionCard';
import useOverviewStats from '../../hooks/useOverviewStats';
import useInvRole from '../../hooks/useInvRole';
import useActiveStore from '../../hooks/useActiveStore';
import { isRootStoreUser } from '../../utils/storeEntity';
import { toInventoryList } from '../../api/inventory-request';
import { INV_ICONS } from '../../constants/icons';
import { getStores } from '../../api/stockLedger';
import { getRequisitions, getPendingIssues } from '../../api/dispensing';
import { getPendingStockAdjustments } from '../../api/stockAdjustment';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { INV_SPACING } from '../../theme';

export default function StockOverview() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const base = url.replace(/\/$/, '');
  // Settled by the time this renders — InvRoleRoute holds the spinner until then.
  const { can } = useInvRole();
  const { activeStoreId } = useActiveStore();
  const [storesList, setStoresList] = useState([]);

  const { stats, loading } = useOverviewStats({
    stores: () => getStores().then(d => {
      setStoresList(toInventoryList(d));
      return d;
    }),
    requisitions: () => getRequisitions({ status: 'SUBMITTED' }),
    issues: () => getPendingIssues(),
    adjustments: () => getPendingStockAdjustments(),
  });

  const isParentStoreUser = isRootStoreUser(activeStoreId, storesList);

  // `action` is the permission guarding the route the entry points at — the same
  // convention as NAV_ITEM_ACTIONS in navigation.js, so a tile is shown exactly when
  // the screen behind it would open. Tiles are dropped and pipeline stages dimmed;
  // both counts are permission-dependent, so a partial grid row is expected.

  // Store-independent counts only. Total SKUs / Low stock / Expiring all need a
  // store_id (there is no org-wide balance endpoint), so they live on Stock Balances
  // where a store selector is visible rather than being silently scoped here.
  const STAT_CARDS = [
    { key: 'stores', label: 'Stores', icon: INV_ICONS.stores, tone: 'teal', path: `${base}/stores`, action: INV_ACTIONS.STORE_VIEW },
    { key: 'requisitions', label: 'Open Requisitions', icon: INV_ICONS.requisitions, tone: 'teal', path: `${base}/requisitions?status=SUBMITTED`, action: INV_ACTIONS.REQUISITION_VIEW },
    { key: 'issues', label: 'Pending Issues', icon: INV_ICONS.issues, tone: 'mint', path: `${base}/issues?status=ISSUED`, action: INV_ACTIONS.ISSUE_VIEW },
    { key: 'adjustments', label: 'Pending Adjustments', icon: INV_ICONS.adjustments, tone: 'amber', path: `${base}/adjustments`, action: INV_ACTIONS.ADJUSTMENT_VIEW },
  ].filter(card => can(card.action));

  const QUICK_ACTIONS = [
    { title: 'Store Queues', description: 'Work waiting on each store in one queue', icon: INV_ICONS.storeQueues, path: `${base}/queues`, action: INV_ACTIONS.QUEUES_VIEW },
    { title: 'New Requisition', description: 'Raise an internal stock requisition', icon: INV_ICONS.requisitions, path: `${base}/requisition/new`, action: INV_ACTIONS.REQUISITION_CREATE, hidden: isParentStoreUser },
    { title: 'Stock Transfers', description: 'Move stock between stores and track past transfers', icon: INV_ICONS.transfer, path: `${base}/transfers`, action: INV_ACTIONS.TRANSFER_CREATE },
    { title: 'New Store', description: 'Configure a storage location or warehouse', icon: INV_ICONS.stores, path: `${base}/stores/new`, action: INV_ACTIONS.STORE_CREATE },
    { title: 'Stock Balances', description: 'On-hand, low stock and expiry by store', icon: INV_ICONS.balances, path: `${base}/balances`, action: INV_ACTIONS.BALANCE_VIEW },
    { title: 'Stock Issues', description: 'Confirm dispatch and acknowledge receipt', icon: INV_ICONS.issues, path: `${base}/issues`, action: INV_ACTIONS.ISSUE_VIEW },
    { title: 'Returns', description: 'View, confirm and acknowledge stock returns', icon: INV_ICONS.returns, path: `${base}/returns`, action: INV_ACTIONS.RETURN_VIEW },
    { title: 'Consolidate Requisitions', description: 'Aggregate sub-store demand and raise a PO', icon: INV_ICONS.consolidations, path: `${base}/consolidations`, action: INV_ACTIONS.CONSOLIDATION_CREATE },
    { title: 'Stock Adjustments', description: 'Quantity corrections, change bin, split batches', icon: INV_ICONS.adjustments, path: `${base}/adjustments`, action: INV_ACTIONS.ADJUSTMENT_VIEW },
    { title: 'Goods Discard', description: 'Remove expired or unusable stock', icon: INV_ICONS.goodsDiscard, path: `${base}/goods-discard`, action: INV_ACTIONS.DISCARD_VIEW },
    { title: 'Opening Stock', description: 'Seed a store with its starting balances', icon: INV_ICONS.openingStock, path: `${base}/opening-stocks`, action: INV_ACTIONS.OPENING_STOCK_VIEW },
    // { title: 'Batch Trace', description: 'Trace a batch across the supply chain', icon: INV_ICONS.batchTrace, path: `${base}/batch-trace`, action: INV_ACTIONS.BATCH_TRACE_VIEW },
  ].filter(item => can(item.action) && !item.hidden);

  const PIPELINE = [
    { label: 'Stores', path: `${base}/stores`, action: INV_ACTIONS.STORE_VIEW },
    { label: 'Opening Stock', path: `${base}/opening-stocks`, action: INV_ACTIONS.OPENING_STOCK_VIEW },
    { label: 'Requisition', path: `${base}/requisitions`, action: INV_ACTIONS.REQUISITION_VIEW },
    { label: 'Consolidate', path: `${base}/consolidations`, action: INV_ACTIONS.CONSOLIDATION_CREATE },
    { label: 'Issue', path: `${base}/issues`, action: INV_ACTIONS.ISSUE_VIEW },
    { label: 'Balances', path: `${base}/balances`, action: INV_ACTIONS.BALANCE_VIEW },
    { label: 'Transfer', path: `${base}/transfers`, action: INV_ACTIONS.TRANSFER_CREATE },
    { label: 'Returns', path: `${base}/returns`, action: INV_ACTIONS.RETURN_VIEW },
    { label: 'Ledger', path: `${base}/ledger`, action: INV_ACTIONS.LEDGER_VIEW },
  ].map(stage => ({ ...stage, disabled: !can(stage.action) }));

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <PageHeader
        breadcrumb={['Inventory Suite', 'Inventory Management']}
        title="Overview"
        description="Store and stock operations at a glance"
      />

      {STAT_CARDS.length > 0 && (
        <Grid container spacing={INV_SPACING.grid} sx={{ mb: INV_SPACING.section }}>
          {STAT_CARDS.map((card) => (
            <Grid item xs={6} md={3} key={card.key}>
              <StatCard
                label={card.label}
                value={stats[card.key]}
                icon={card.icon}
                tone={card.tone}
                loading={loading}
                onClick={() => history.push(card.path)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <SectionCard title="Stock Workflow" sx={{ mb: INV_SPACING.section }}>
        <WorkflowPipeline stages={PIPELINE} />
      </SectionCard>

      {QUICK_ACTIONS.length > 0 && (
        <SectionCard title="Quick Actions">
          <Grid container spacing={INV_SPACING.grid}>
            {QUICK_ACTIONS.map((action) => (
              <QuickActionCard
                key={action.title}
                icon={action.icon}
                title={action.title}
                description={action.description}
                onClick={() => history.push(action.path)}
                xs={12}
                sm={6}
                md={4}
                lg={3}
              />
            ))}
          </Grid>
        </SectionCard>
      )}
    </Box>
  );
}
